import React from "react";
import {
  Check,
  Sparkles,
  User,
  FileText,
  Image as ImageIcon,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import type { AIMessage } from "@/hooks/use-ai-form-assistant";

interface AIMessageBubbleProps {
  message: AIMessage;
}

export function AIMessageBubble({ message }: AIMessageBubbleProps) {
  const isUser = message.role === "user";

  return (
    <div
      className={`flex min-w-0 gap-2 ${isUser ? "flex-row-reverse" : "flex-row"}`}
    >
      <div
        className={`shrink-0 w-6 h-6 rounded-full flex items-center justify-center ${
          isUser ? "bg-primary/10 text-primary" : "bg-primary/5 text-primary"
        }`}
      >
        {isUser ? <User size={12} /> : <Sparkles size={12} />}
      </div>

      <div
        className={`min-w-0 max-w-[calc(100%-2rem)] rounded-xl px-3 py-2 text-sm leading-relaxed sm:max-w-[85%] ${
          isUser
            ? "bg-primary text-primary-foreground"
            : "bg-slate-100 text-slate-800"
        }`}
      >
        <p className="whitespace-pre-wrap [overflow-wrap:anywhere]">
          {message.content}
        </p>

        {message.attachments && message.attachments.length > 0 && (
          <div className="mt-1.5 flex flex-wrap gap-1">
            {message.attachments.map((att, i) => {
              const isImage = att.mimeType.startsWith("image/");
              const Icon = isImage ? ImageIcon : FileText;
              return (
                <div
                  key={`${att.fileName}-${i}`}
                  className={`flex min-w-0 max-w-full items-center gap-1 rounded px-1.5 py-0.5 text-xs ${
                    isUser
                      ? "bg-white/15 text-primary-foreground"
                      : "bg-white border border-gray-200 text-gray-700"
                  }`}
                  title={att.fileName}
                >
                  <Icon size={10} className="shrink-0" />
                  <span className="min-w-0 truncate max-w-[120px]">
                    {att.fileName}
                  </span>
                </div>
              );
            })}
          </div>
        )}

        {message.appliedChanges && (
          <Badge
            variant="outline"
            className="mt-1.5 bg-green-50 text-green-700 border-green-200 text-xs"
          >
            <Check size={10} className="mr-1" />
            Changes applied
          </Badge>
        )}
      </div>
    </div>
  );
}
